import React, { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { VelocityIndicator } from '../../../atoms/VelocityIndicator';
import { useSphereStatus } from '../../../../hooks/useSphereStatus';
import { useStatusCheckIn } from '../../../../hooks/useStatusCheckIn';
import CheckInFormContent from '../../StatusCheckInDialog/CheckInFormContent';
import SphereTimelineEventMolecule from '../../SphereTimelineEvent/SphereTimelineEventMolecule';
import { getStatusColor, type ExtendedStatusValue } from '../../../../utils/statusColors';
import { formatRelativeTime } from '../../../../utils/arc/dateUtils';
import type { StatusFactorSummary, StatusValue } from 'types/spherePoints';
import type { VelocityBucketKey } from 'types/sphereMetrics';
import {
  useStatusTimeline,
  loadStatusTimelineFilters,
  saveStatusTimelineFilters,
  type StatusTimelineEventType,
} from '../../../../hooks/useStatusTimeline';
import { isFactorOnCard, resolveFactorDisplayName } from '../../../../utils/factorDisplay';
import './StatusSummaryMode.scss';

export interface StatusSummaryModeProps {
  /** Currently active sphere ID */
  activeSphereId: string;
  /** Additional CSS classes */
  className?: string;
}

/** Status sparkle icon SVG */
const StatusSparkle: React.FC<{ color: string; size?: number }> = ({
  color,
  size = 32,
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 27 26"
    fill="none"
    aria-hidden="true"
    className="status-summary-mode__sparkle"
  >
    <g clipPath="url(#status-panel-clip)">
      <path
        d="M6.72039 12.8585L11.5481 10.986L13.3037 5.83639C13.4867 5.29933 14.1988 5.29933 14.3819 5.83639L16.1374 10.986L20.9651 12.8585C21.4687 13.0538 21.4687 13.8134 20.9651 14.0087L16.1374 15.8812L14.3819 21.0308C14.1988 21.5679 13.4867 21.5679 13.3037 21.0308L11.5481 15.8812L6.72039 14.0087C6.2169 13.8134 6.2169 13.0538 6.72039 12.8585Z"
        fill={color}
        stroke={color}
      />
    </g>
    <defs>
      <clipPath id="status-panel-clip">
        <rect
          width="26"
          height="26"
          fill="white"
          transform="translate(26.5) rotate(90)"
        />
      </clipPath>
    </defs>
  </svg>
);

/** Threshold for auto-collapsing the factor list */
const FACTOR_COLLAPSE_THRESHOLD = 5;

/** Filter chip configuration for Zone 3 event type filters */
const STATUS_FILTER_CHIPS: {
  eventType: StatusTimelineEventType;
  labelKey: string;
  icon: string;
}[] = [
  { eventType: 'status_propagated', labelKey: 'statusPanel.filters.status', icon: 'pi-circle-fill' },
  { eventType: 'check_in', labelKey: 'statusPanel.filters.checkIns', icon: 'pi-check' },
  { eventType: 'velocity_changed', labelKey: 'statusPanel.filters.velocity', icon: 'pi-chart-line' },
  // One chip, not two: urgency and gravity are both edge-subject aspects now, so they share the
  // `aspect_observation` entry type and cannot be filtered apart here.
  { eventType: 'aspect_observation', labelKey: 'statusPanel.filters.aspectValues', icon: 'pi-exclamation-circle' },
];

/**
 * StatusSummaryMode - Displays the status tool panel.
 * Zone 1: Aggregate status summary with velocity and collapsible factor breakdown.
 * Zone 2: Quick check-in form for pending periods.
 * Zone 3: Event type filter chips + status history timeline.
 */
const StatusSummaryMode: React.FC<StatusSummaryModeProps> = ({
  activeSphereId,
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const { status, loading, error, point, refetch: refetchStatus } = useSphereStatus(activeSphereId);

  // EAINT-750: hide factors the sphere owner opted out of showing on cards.
  // Reads from the status-point summary; `useSphereStatus` doesn't surface
  // `factorDefinitions`, so we pass no live lookup — the summary's own
  // `showOnCards` (rewritten by updateStatusFactorRoute on toggle) is
  // authoritative until the next recompute.
  const factors = ((point?.currentValue?.factors || []) as StatusFactorSummary[])
    .filter((f) => isFactorOnCard(f));

  const [factorsExpanded, setFactorsExpanded] = useState<boolean>(
    () => factors.length <= FACTOR_COLLAPSE_THRESHOLD
  );

  // Velocity data comes from the API but isn't fully typed in the local hook interface
  const velocity = (point?.currentValue as unknown as Record<string, unknown>)
    ?.velocity as
    | { score: number; direction: string; bucket: string }
    | undefined;
  const computedAt = point?.computedAt;

  const updatedTimeStr = useMemo(() => {
    if (!computedAt) return null;
    return formatRelativeTime(new Date(computedAt), i18n.language);
  }, [computedAt, i18n.language]);

  const velocityArrow = useMemo(() => {
    if (!velocity?.direction) return null;
    if (velocity.direction === 'improving') return '\u2191';
    if (velocity.direction === 'declining') return '\u2193';
    return '\u2192';
  }, [velocity?.direction]);

  // Ref to hold timeline refresh — avoids hook ordering dependency
  const refreshTimelineRef = useRef<() => void>(() => undefined);

  // Zone 2: Quick Check-In — refetch status + timeline after submission
  const handleCheckInComplete = useCallback(() => {
    refetchStatus();
    refreshTimelineRef.current();
  }, [refetchStatus]);

  const {
    pendingCheckIn,
    pendingCount,
    loading: checkInLoading,
    isSubmitting,
    error: checkInError,
    submitCheckIn,
  } = useStatusCheckIn({
    sphereId: activeSphereId,
    factors,
    onCheckInComplete: handleCheckInComplete,
  });

  const checkInFactorName = useMemo(() => {
    if (!pendingCheckIn) return '';
    const key = `statusPopover.factorNames.${pendingCheckIn.factorType}`;
    const translated = t(key);
    return translated === key
      ? pendingCheckIn.factorType.replaceAll('_', ' ')
      : translated;
  }, [pendingCheckIn, t]);

  const checkInDueLabel = useMemo(() => {
    if (!pendingCheckIn) return '';
    if (pendingCheckIn.isOverdue) return t('statusPanel.checkIn.overdue');
    return t('statusPanel.checkIn.dueDate', {
      date: formatRelativeTime(new Date(pendingCheckIn.dueAt), i18n.language),
    });
  }, [pendingCheckIn, t, i18n.language]);

  const handleCheckInCancel = useCallback(() => {
    // No-op for inline form — the section is always visible when pending
  }, []);

  // Zone 3: Event type filter chips
  const [enabledEventTypes, setEnabledEventTypes] = useState<StatusTimelineEventType[]>(
    () => loadStatusTimelineFilters(activeSphereId)
  );

  useEffect(() => {
    setEnabledEventTypes(loadStatusTimelineFilters(activeSphereId));
  }, [activeSphereId]);

  const handleFilterToggle = useCallback(
    (eventType: StatusTimelineEventType) => {
      setEnabledEventTypes((prev) => {
        const isEnabled = prev.includes(eventType);
        let updated: StatusTimelineEventType[];
        if (isEnabled && prev.length > 1) {
          updated = prev.filter((et) => et !== eventType);
        } else if (isEnabled) {
          return prev;
        } else {
          updated = [...prev, eventType];
        }
        saveStatusTimelineFilters(activeSphereId, updated);
        return updated;
      });
    },
    [activeSphereId]
  );

  const allFilterTypes = useMemo(
    () => STATUS_FILTER_CHIPS.map((c) => c.eventType),
    []
  );
  const filtersReduced = enabledEventTypes.length < allFilterTypes.length;

  const handleShowAllFilters = useCallback(() => {
    setEnabledEventTypes(allFilterTypes);
    saveStatusTimelineFilters(activeSphereId, allFilterTypes);
  }, [activeSphereId, allFilterTypes]);

  // Roving tabindex for filter chips keyboard navigation
  const [focusedChipIndex, setFocusedChipIndex] = useState(0);
  const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleChipKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      const chipCount = STATUS_FILTER_CHIPS.length;
      let nextIndex: number;

      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (focusedChipIndex + 1) % chipCount;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (focusedChipIndex - 1 + chipCount) % chipCount;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = chipCount - 1;
      } else {
        return;
      }

      setFocusedChipIndex(nextIndex);
      chipRefs.current[nextIndex]?.focus();
    },
    [focusedChipIndex]
  );

  // Focus management for Zone 2 (check-in section)
  const zone2Ref = useRef<HTMLDivElement>(null);
  const prevPendingCheckInRef = useRef<typeof pendingCheckIn>(null);
  const [liveAnnouncement, setLiveAnnouncement] = useState('');

  useEffect(() => {
    const prev = prevPendingCheckInRef.current;
    prevPendingCheckInRef.current = pendingCheckIn;

    // Focus Zone 2 when it first appears
    if (!prev && pendingCheckIn && zone2Ref.current) {
      zone2Ref.current.focus();
    }
  }, [pendingCheckIn]);

  // Announce check-in success via live region
  const handleSubmitCheckIn = useCallback(
    async (statusValue: StatusValue, comment?: string) => {
      await submitCheckIn(statusValue, comment);
      setLiveAnnouncement(t('statusPanel.a11y.checkInSubmitted'));
      // Clear after screen reader has time to announce
      setTimeout(() => setLiveAnnouncement(''), 3000);
    },
    [submitCheckIn, t]
  );

  // Zone 3: Timeline data with infinite scroll
  const {
    events: timelineEvents,
    loading: timelineLoading,
    error: timelineError,
    hasMore: timelineHasMore,
    fetchMore: fetchMoreTimeline,
    refresh: refreshTimeline,
  } = useStatusTimeline({
    sphereId: activeSphereId,
    enabledEventTypes,
  });

  // Keep ref in sync so handleCheckInComplete can call it
  refreshTimelineRef.current = refreshTimeline;

  // Refs for infinite scroll observers
  const timelineRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Infinite scroll: IntersectionObserver triggers fetchMore when sentinel visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = timelineRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && timelineHasMore && !timelineLoading) {
          fetchMoreTimeline();
        }
      },
      { root: container, rootMargin: '100px' }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [timelineLoading, timelineHasMore, fetchMoreTimeline]);

  // Auto-fetch fallback: ResizeObserver detects when content doesn't fill container
  useEffect(() => {
    const el = timelineRef.current;
    if (!el) return;

    const checkAndFetch = () => {
      if (el.scrollHeight <= el.clientHeight && timelineHasMore && !timelineLoading) {
        fetchMoreTimeline();
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      checkAndFetch();
    });

    resizeObserver.observe(el);
    checkAndFetch();

    return () => resizeObserver.disconnect();
  }, [timelineLoading, timelineHasMore, fetchMoreTimeline]);

  const statusColor = getStatusColor(status || 'unknown');
  const statusLabel = t(`statusPanel.statusValues.${status || 'unknown'}`);

  const titleBar = (
    <div className="status-summary-mode__title-bar">
      <i className="pi pi-chart-bar" aria-hidden="true" />
      <span>{t('statusPanel.title')}</span>
    </div>
  );

  if (loading) {
    return (
      <section className={`status-summary-mode ${className}`} aria-label={t('statusPanel.a11y.loadingStatus')}>
        {titleBar}
        {/* Zone 1 skeleton: status header + factor rows */}
        <div className="status-summary-mode__zone1">
          <div className="status-summary-mode__skeleton-zone1">
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--icon" />
            <div className="status-summary-mode__skeleton-zone1-text">
              <div className="status-summary-mode__skeleton status-summary-mode__skeleton--text" />
              <div className="status-summary-mode__skeleton status-summary-mode__skeleton--text-short" />
            </div>
          </div>
          <div className="status-summary-mode__skeleton status-summary-mode__skeleton--text-short" />
          <div className="status-summary-mode__skeleton-factors">
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--factor" />
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--factor" />
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--factor" />
          </div>
        </div>
        {/* Zone 3 skeleton: filter chips + timeline events */}
        <div className="status-summary-mode__zone3">
          <div className="status-summary-mode__skeleton-chips">
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--chip" />
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--chip" />
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--chip" />
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--chip" />
          </div>
          <div className="status-summary-mode__skeleton-events">
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--event" />
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--event" />
            <div className="status-summary-mode__skeleton status-summary-mode__skeleton--event" />
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={`status-summary-mode ${className}`} aria-label={t('statusPanel.title')}>
        {titleBar}
        <div className="status-summary-mode__error" role="alert">
          <i className="pi pi-exclamation-triangle" aria-hidden="true" />
          <p>{t('statusPanel.error.fetchFailed')}</p>
          <button
            type="button"
            className="status-summary-mode__retry-button"
            onClick={refetchStatus}
          >
            {t('statusPanel.error.retry')}
          </button>
        </div>
      </section>
    );
  }

  if (!point) {
    return (
      <section className={`status-summary-mode ${className}`} aria-label={t('statusPanel.title')}>
        {titleBar}
        <div className="status-summary-mode__empty">
          <i className="pi pi-info-circle" aria-hidden="true" />
          <p>{t('statusPanel.empty.noStatusPoint')}</p>
          <button
            type="button"
            className="status-summary-mode__configure-button"
          >
            {t('statusPanel.empty.configure')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className={`status-summary-mode ${className}`} aria-label={t('statusPanel.title')}>
      {titleBar}

      {/* Screen reader live region for dynamic announcements */}
      <div className="sr-only" aria-live="polite" aria-atomic="true">
        {liveAnnouncement}
      </div>

      {/* Zone 1: Status Summary Header */}
      <section className="status-summary-mode__zone1" aria-label={t('statusPanel.a11y.statusSummary')}>
        <div className="status-summary-mode__status-header">
          <StatusSparkle color={statusColor} />
          <div className="status-summary-mode__status-info">
            <span
              className="status-summary-mode__status-label"
              style={{ color: statusColor }}
            >
              {statusLabel}
            </span>
            {velocityArrow && velocity?.bucket && (
              <span
                className={`status-summary-mode__velocity status-summary-mode__velocity--${velocity.direction}`}
              >
                {velocityArrow} {t(`velocity.buckets.${velocity.bucket}`)}
              </span>
            )}
          </div>
        </div>
        {updatedTimeStr && (
          <div className="status-summary-mode__updated">
            {t('statusPanel.summary.updatedAgo', { time: updatedTimeStr })}
          </div>
        )}

        {/* Factor Breakdown List */}
        {factors.length > 0 && (
          <div className="status-summary-mode__factors">
            <div className="status-summary-mode__factors-header">
              <span className="status-summary-mode__factors-title">
                {t('statusPanel.factors.title')}
              </span>
            </div>
            <ul id={`status-factor-list-${activeSphereId}`} className="status-summary-mode__factor-list" aria-label={t('statusPanel.a11y.factorBreakdown')}>
              {(factorsExpanded
                ? factors
                : factors.slice(0, FACTOR_COLLAPSE_THRESHOLD)
              ).map((factor) => {
                const factorStatus = factor.status || 'unknown';
                const factorColor = getStatusColor(factorStatus as ExtendedStatusValue);
                const factorStatusLabel = t(
                  `statusPanel.statusValues.${factorStatus}`
                );
                // Prefer the owner-set name (metadata.name surfaced on the
                // summary by calculateStatusPoint). Fall back to the localized
                // type label so legacy summaries that lack the name still
                // render something, and multiple manual factors no longer
                // all appear as "Manual".
                const factorNameKey = `statusPopover.factorNames.${factor.factorType}`;
                const factorTypeLabel = t(factorNameKey) === factorNameKey
                  ? factor.factorType.replaceAll('_', ' ')
                  : t(factorNameKey);
                const factorName = resolveFactorDisplayName(factor, factorTypeLabel);
                const hasVelocity =
                  factor.velocityScore !== undefined &&
                  factor.velocityBucket !== undefined;

                const isNotDeployed = factor.deployed === false;

                return (
                  <li
                    key={factor.id}
                    className="status-summary-mode__factor-row"
                  >
                    <span
                      className="status-summary-mode__factor-dot"
                      style={{ backgroundColor: isNotDeployed ? 'var(--surface-400, #9ca3af)' : factorColor }}
                      aria-hidden="true"
                    />
                    <span className="status-summary-mode__factor-name">
                      {factorName}
                    </span>
                    {isNotDeployed ? (
                      <span className="status-summary-mode__factor-not-deployed">
                        {t('statusPanel.factors.notDeployed')}
                      </span>
                    ) : (
                      <>
                        <span
                          className="status-summary-mode__factor-status"
                          style={{ color: factorColor }}
                        >
                          {factorStatusLabel}
                        </span>
                        {hasVelocity && (
                          <VelocityIndicator
                            score={factor.velocityScore ?? 0}
                            bucket={factor.velocityBucket as VelocityBucketKey}
                            size="small"
                          />
                        )}
                      </>
                    )}
                    <span className="status-summary-mode__factor-weight">
                      {t('statusPanel.factors.weight', {
                        weight: factor.weight,
                      })}
                    </span>
                  </li>
                );
              })}
            </ul>
            {factors.length > FACTOR_COLLAPSE_THRESHOLD && (
              <button
                type="button"
                className="status-summary-mode__factors-toggle"
                onClick={() => setFactorsExpanded((prev) => !prev)}
                aria-expanded={factorsExpanded}
                aria-controls={`status-factor-list-${activeSphereId}`}
              >
                {factorsExpanded
                  ? t('statusPanel.factors.collapse')
                  : t('statusPanel.factors.showAll', {
                      count: factors.length,
                    })}
              </button>
            )}
          </div>
        )}

        {/* No factors empty state */}
        {factors.length === 0 && (
          <div className="status-summary-mode__no-factors">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>{t('statusPanel.empty.noFactors')}</span>
          </div>
        )}

        {/* All check-ins complete badge */}
        {!checkInLoading && pendingCount === 0 && factors.length > 0 && (
          <div className="status-summary-mode__all-complete">
            <i className="pi pi-check-circle" aria-hidden="true" />
            <span>{t('statusPanel.checkIn.allComplete')}</span>
          </div>
        )}
      </section>

      {/* Zone 2: Quick Check-In */}
      {pendingCheckIn && (
        <section
          ref={zone2Ref}
          className={`status-summary-mode__zone2${
            pendingCheckIn.isOverdue
              ? ' status-summary-mode__zone2--overdue'
              : ''
          }`}
          aria-label={t('statusPanel.a11y.checkInSection')}
          tabIndex={-1}
        >
          <div className="status-summary-mode__checkin-header">
            <span className="status-summary-mode__checkin-title">
              {t('statusPanel.checkIn.title', {
                factorName: checkInFactorName,
              })}
            </span>
            <span
              className={`status-summary-mode__checkin-due${
                pendingCheckIn.isOverdue
                  ? ' status-summary-mode__checkin-due--overdue'
                  : ''
              }`}
            >
              {checkInDueLabel}
            </span>
          </div>
          {pendingCount > 1 && (
            <div className="status-summary-mode__checkin-count">
              {t('statusPanel.checkIn.pendingCount', {
                current: 1,
                total: pendingCount,
              })}
            </div>
          )}
          <CheckInFormContent
            defaultStatus={pendingCheckIn.currentStatus}
            velocityScore={pendingCheckIn.velocityScore}
            velocityBucket={pendingCheckIn.velocityBucket}
            isSubmitting={isSubmitting}
            error={checkInError}
            onSubmit={handleSubmitCheckIn}
            onCancel={handleCheckInCancel}
            className="status-summary-mode__checkin-form"
          />
        </section>
      )}

      {/* Zone 3: Status History */}
      <section className="status-summary-mode__zone3" aria-label={t('statusPanel.timeline.title')}>
        <div className="status-summary-mode__zone3-header">
          <span className="status-summary-mode__zone3-title">
            {t('statusPanel.timeline.title')}
          </span>
        </div>
        <div
          className="status-summary-mode__filter-chips"
          role="toolbar"
          aria-label={t('statusPanel.a11y.filterToolbar')}
          onKeyDown={handleChipKeyDown}
        >
          {STATUS_FILTER_CHIPS.map((chip, index) => {
            const isActive = enabledEventTypes.includes(chip.eventType);
            return (
              <button
                key={chip.eventType}
                ref={(el) => { chipRefs.current[index] = el; }}
                type="button"
                className={`status-summary-mode__filter-chip${
                  isActive ? ' status-summary-mode__filter-chip--active' : ''
                }`}
                onClick={() => handleFilterToggle(chip.eventType)}
                onFocus={() => setFocusedChipIndex(index)}
                tabIndex={index === focusedChipIndex ? 0 : -1}
                aria-pressed={isActive}
              >
                <i className={`pi ${chip.icon}`} aria-hidden="true" />
                {t(chip.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Timeline error state */}
        {timelineError && !timelineLoading && (
          <div className="status-summary-mode__timeline-error" role="alert">
            <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            <p>{t('statusPanel.error.timelineFailed')}</p>
            <button
              type="button"
              className="status-summary-mode__retry-button"
              onClick={refreshTimeline}
            >
              {t('statusPanel.error.retry')}
            </button>
          </div>
        )}

        {/* Timeline scroll area */}
        <div ref={timelineRef} className="status-summary-mode__timeline" aria-label={t('statusPanel.a11y.timeline')} role="feed">
          {timelineEvents.map((event) => (
            <div key={event.id} className="status-summary-mode__timeline-event">
              <SphereTimelineEventMolecule event={event} />
            </div>
          ))}

          {/* Sentinel for infinite scroll detection */}
          <div ref={sentinelRef} className="status-summary-mode__scroll-sentinel" />

          {/* Loading spinner during pagination fetch */}
          {timelineLoading && timelineEvents.length > 0 && (
            <div className="status-summary-mode__timeline-loading" role="status">
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
              <span className="sr-only">{t('statusPanel.a11y.loadingStatus')}</span>
            </div>
          )}

          {/* End of timeline indicator */}
          {!timelineHasMore && timelineEvents.length > 0 && (
            <div className="status-summary-mode__timeline-end">
              {t('statusPanel.timeline.noMoreEvents')}
            </div>
          )}

          {/* Empty state: no events */}
          {!timelineLoading && timelineEvents.length === 0 && (
            <div className="status-summary-mode__timeline-empty">
              <i className="pi pi-clock" aria-hidden="true" />
              {filtersReduced ? (
                <>
                  <p>{t('statusPanel.timeline.noFilterMatch')}</p>
                  <button
                    type="button"
                    className="status-summary-mode__timeline-show-all"
                    onClick={handleShowAllFilters}
                  >
                    {t('statusPanel.timeline.showAll')}
                  </button>
                </>
              ) : (
                <p>{t('statusPanel.timeline.noEvents')}</p>
              )}
            </div>
          )}
        </div>
      </section>
    </section>
  );
};

export default StatusSummaryMode;
